// src/redux/slices/studentMetricsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ANALYTICS_BASE_URL } from "../../../services/configUrls";


// -----------------------------------------------------
// 1️⃣ QUICK API — Fast load (shows loading)
// -----------------------------------------------------
export const fetchStudentMetricsQuick = createAsyncThunk(
 "studentMetrics/fetchQuick",
async (_, { rejectWithValue, getState }) => {
try {
 const accessToken = localStorage.getItem("accessToken");
 const currentAuthorise = getState().authorise.userRole;


 console.log("Fetching QUICK student metrics...");


 const response = await axios.get(
 `${ANALYTICS_BASE_URL}/internship/students_metrics_spoc4`,
 {
 headers: {
 Authorization: `Bearer ${accessToken}`,
 "Content-Type": "application/json",
 },
 }
 );


 return {
 data: response.data,
 authorise: currentAuthorise,
 timestamp: new Date().toISOString(),
 databasetime: response.data.updated_at
 };
 } catch (error) {
 if (error.response?.status === 401) {
 localStorage.clear();
 return rejectWithValue("Unauthorized access. Please login again.");
 }
 return rejectWithValue("Your dashboard data is currently being processed. This may take a few hours to complete");
 }
 }
);



// -----------------------------------------------------
// 2️⃣ FULL API — Background refresh (NO loading)
// -----------------------------------------------------
export const fetchStudentMetricsFull = createAsyncThunk(
 "studentMetrics/fetchFull",
 async (_, { rejectWithValue, getState }) => {
 try {
 const accessToken = localStorage.getItem("accessToken");
 const currentAuthorise = getState().authorise.userRole;


 console.log("Fetching FULL student metrics in background...");


 const response = await axios.get(
 `${ANALYTICS_BASE_URL}/internship/students_metrics_spoc_with`,
 {
 headers: {
 Authorization: `Bearer ${accessToken}`,
 "Content-Type": "application/json",
 },
 }
 );

 return {
 data: response.data,
 authorise: currentAuthorise,
 timestamp: new Date().toISOString(),
 databasetime: response.data.updated_at
 };
 } catch (error) {
 if (error.response?.status === 401) {
 localStorage.clear();
 return rejectWithValue("Unauthorized access. Please login again.");
 }
 return rejectWithValue("Your dashboard data is currently being processed. This may take a few hours to complete");
 }
 }
);


// -----------------------------------------------------
// Slice
// -----------------------------------------------------
const studentMetricsSlice = createSlice({
 name: "studentMetrics",
 initialState: {
 data: null,
 loading: false,
 error: null,
 lastFetched: null,
 storedAuthorise: null,
 shouldRefresh: false,
 },


 reducers: {
 clearMetricsData: (state) => {
 state.data = null;
 state.lastFetched = null;
 },
 resetShouldRefresh: (state) => {
 state.shouldRefresh = false;
 },
 triggerRefresh: (state) => {
 state.shouldRefresh = true;
 },
 },


 extraReducers: (builder) => {
 builder
 // -----------------------------------------------------
 // QUICK API (shows loading)
 // -----------------------------------------------------
 .addCase(fetchStudentMetricsQuick.pending, (state) => {
 state.loading = true;
 state.error = null;
 })
 .addCase(fetchStudentMetricsQuick.fulfilled, (state, action) => {
 state.loading = false;
 state.data = action.payload.data;
 state.lastFetched = action.payload.databasetime;
 state.storedAuthorise = action.payload.authorise;
 console.log("Quick student metrics loaded.");
 })
 .addCase(fetchStudentMetricsQuick.rejected, (state, action) => {
 state.loading = false;
 state.error = action.payload || "Failed to load student metrics";
 console.log("Quick metrics failed:", action.payload);
 })



 // -----------------------------------------------------
 // FULL API (NO loading)
 // -----------------------------------------------------
 .addCase(fetchStudentMetricsFull.fulfilled, (state, action) => {
  state.data = action.payload.data; // silent overwrite
  state.lastFetched = action.payload.databasetime;
  console.log("Full student metrics updated silently.");
 })
 .addCase(fetchStudentMetricsFull.rejected, (state, action) => {
  console.log("Full metrics background update failed.", action.payload);
 })



 // -----------------------------------------------------
 // Listen for authorise role changes
 // -----------------------------------------------------
 .addMatcher(
 (action) => action.type.startsWith("authorise/"),
 (state, action) => {
  if (action.type === "authorise/setUserRole") {
  const newRole = action.payload;
  const validRoles = ["spoc", "dspoc", "leaders", "tpo", "leader"];


 if (validRoles.includes(newRole)) {
if (state.storedAuthorise !== newRole) {
 state.shouldRefresh = true;
 }
 state.storedAuthorise = newRole;
 }
 } else if (action.type === "authorise/clearAuthorise") {
 state.storedAuthorise = "";
 state.shouldRefresh = true;
 }
 }
 );
 },
});



export const {
 clearMetricsData,
 resetShouldRefresh,
 triggerRefresh,
} = studentMetricsSlice.actions;



// Selectors
export const selectStudentMetricsData = (state) => state.studentMetrics.data;
export const selectStudentMetricsLoading = (state) =>
 state.studentMetrics.loading;
export const selectStudentMetricsError = (state) => state.studentMetrics.error;
export const selectLastFetched = (state) => state.studentMetrics.lastFetched;
export const selectStoredAuthorise = (state) =>
 state.studentMetrics.storedAuthorise;
export const selectShouldRefresh = (state) =>
 state.studentMetrics.shouldRefresh;


export default studentMetricsSlice.reducer;